import React, { useState, useRef, useEffect } from 'react';
import MapTo from "../../utils/mapto";
// import useEmblaCarousel from "embla-carousel-react";
import CarouselArrowButton from "./CarouselArrowButton";
import CarouselDotButton from "./CarouselDotButton";
import GTM from '../../utils/GTM';
const Carousel = ({ className, items, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next'); // Track slide direction for animations
  const [maxHeight, setMaxHeight] = useState(0);
  const carouselRef = useRef(null);
  const slideRefs = useRef([]);

  const nextStyle = {
    transition: 'all 0.7s ease-in-out',
    right: 0,
    opacity: 1,
    zIndex: 1,
    transform: 'translateY(-50%) translateX(0%) scale(0.8)',
    filter: 'blur(2px)',
  }

  const prevStyle = {
    left: 0,
    opacity: 1,
    zIndex: 1,
    transform: 'translateY(-50%) translateX(0%) scale(0.8)',
    filter: 'blur(2px)',
  }

  const nowStyle = {
    left: '50%',
    opacity: 1,
    zIndex: 5,
    transform: 'translateY(-50%) translateX(-50%) scale(1)',
    filter: 'brightness(100%)',
  }

  // const arrowStyle = {
  //   width: '3rem',
  //   height: '3rem',
  //   transition: 'all 0.3s ease',
  //   zIndex: 10,
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   backdropFilter: 'blur(40px)',
  //   boxShadow: '0 20px 50px -20px rgba(0, 0, 0, 0.2),  inset 0 0 50px 0 rgba(0, 0, 0, 0.05)',
  // }
  

  // Initialize slide refs array
  useEffect(() => {
    slideRefs.current = slideRefs.current.slice(0, items.length);
  }, [items]);

  // Calculate max height of all slides
  useEffect(() => {
    if (!isMounted || !slideRefs.current.length) return;

    const calculateMaxHeight = () => {
      const heights = slideRefs.current.map(ref => {
        if (!ref) return 0;
        return ref.offsetHeight;
      });
      
      const max = Math.max(...heights);
      setMaxHeight(max);
    };

    // Initial calculation
    calculateMaxHeight();

    // Recalculate on window resize
    const handleResize = () => {
      calculateMaxHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMounted, items]);

  // Set isMounted to true after component mounts (client-side only)
  useEffect(() => {
    setIsMounted(true);
    
    // Check if device is mobile
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Function to get content from one item 
  const getContent = (item) => {
    if (item) {
      return <MapTo items={item} key={item.id} className={className} />
    }
    return null;
  };

  // Rearrange slides order: 1,3,2
  const reorderedSlides = [...items];
  if (reorderedSlides.length >= 3) {
    const temp = reorderedSlides[1];
    reorderedSlides[1] = reorderedSlides[2];
    reorderedSlides[2] = temp;
  }

  const calculateNextSlide = (currentSlide, direction) => {
    return direction === 'next'
      ? (currentSlide + 1) % reorderedSlides.length
      : (currentSlide - 1 + reorderedSlides.length) % reorderedSlides.length;
  }

  const handleSlideChange = (direction) => {
    const nextSlide = calculateNextSlide(currentSlide, direction);
    
    // Set the slide direction for animation
    setSlideDirection(direction);

    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => {
        const next = calculateNextSlide(prev, direction);
        return next;
      });
      setTimeout(() => setIsAnimating(false), 1000);
    }
  };

  const nextSlide = () => handleSlideChange('next');
  const prevSlide = () => handleSlideChange('prev');

  const handleMouseDown = (e) => {
    setMouseEnd(null);
    setMouseStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (mouseStart !== null) {
      setMouseEnd(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (!mouseStart || !mouseEnd) {
      setMouseStart(null);
      return;
    }

    const distance = mouseStart - mouseEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) < minSwipeDistance) {
      setMouseStart(null);
      return;
    }

    distance > 0 ? nextSlide() : prevSlide();
    setMouseStart(null);
  };

  // const getArrowClass = () => {
  //   if (theme === 'light') {
  //     return `carousel__arrow flow circle-icon cursor-pointer rounded-full border-gradient-button disabled:cursor-not-allowed hover:bg-soft_green/30 hover:border-gradient-button-hover focus:bg-soft_green/30 focus:shadow-[0px_0px_50px_0px_rgba(0,0,0,0.05)_inset,0px_20px_50px_-20px_rgba(0,0,0,0.20)]`;
  //   }
  //   // Dark theme
  //   return `carousel__arrow flow circle-icon cursor-pointer disabled:cursor-not-allowed border-gradient-button hover:border-gradient-button-hover bg-[#0B302C33]`;
  // };

  const getSlidePosition = (index) => {
    // On mobile, only show the current slide
    if (isMobile) {
      const isNow = index === currentSlide;
      return { isNow, isPrev: false, isNext: false };
    }
    
    // On desktop, show the 3D cascade effect
    const isNow = index === currentSlide;
    const isPrev = index === (currentSlide === 0 ? reorderedSlides.length - 1 : currentSlide - 1);
    const isNext = index === (currentSlide === reorderedSlides.length - 1 ? 0 : currentSlide + 1);

    return { isNow, isPrev, isNext };
  };

  // Get animation class based on slide direction
  const getMobileAnimationClass = (index) => {
    if (!isMobile || index !== currentSlide) return '';
    
    return slideDirection === 'next' 
      ? 'slide-in-right' 
      : 'slide-in-left';
  };

  // Render a placeholder during server-side rendering to avoid hydration issues
  if (!isMounted) {
    return <div className={`cascade-slider_container ${className || ""}`}></div>;
  }

  return (
    <div className='flex flex-col'>
      {/* Add dynamic style tag for animations */}
      <style jsx>{`
        @keyframes slideInRight {
          0% {  
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0%);
            opacity: 1;
          }
        }
        
        @keyframes slideInLeft {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0%);
            opacity: 1;
          }
        }
        
        .slide-in-right {
          animation: slideInRight 0.5s ease-in-out forwards;
        }
        
        .slide-in-left {
          animation: slideInLeft 0.5s ease-in-out forwards;
        }
      `}</style>
      
      <div
        className={`cascade-slider_container w-full overflow-hidden ${className || ""}`}
        id="cascade-slider"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        ref={carouselRef}
        style={{ height: !isMobile ? (maxHeight > 0 ? `${maxHeight}px` : 'auto') : 'auto' }}
      >
        <div className="flex relative w-full h-full">
          {reorderedSlides.map((slide, index) => {
            const { isNow, isPrev, isNext } = getSlidePosition(index);
            
            // On mobile, only render the current slide
            if (isMobile && !isNow) {
              return null;
            }

            return (
              <div
                key={index}
                ref={el => slideRefs.current[index] = el}
                className={`flex-1 lg:absolute top-[50px] lg:top-[50%] left-[50%] transform-translate-x-[-50%] transform-scale-30 transition-all duration-700 ease-in-out opacity-0 z-[-1] text-center cascade-slider_item${isNow ? ' now ' : ''}${isPrev ? ' prev' : ''}${isNext ? ' next' : ''} ${getMobileAnimationClass(index)}`}
                onClick={(event) => {
					if (isPrev) {
						prevSlide();
            GTM.carouselClick(event);
					}
					if (isNext) {
						nextSlide();
            GTM.carouselClick(event);
					}
                }}
                style={{ cursor: (isPrev || isNext) ? 'pointer' : 'default', ...(isNext ? nextStyle : {}), ...(isPrev ? prevStyle : {}), ...(isNow ? nowStyle : {}),
                height: !isMobile ? (maxHeight > 0 ? `${maxHeight}px` : 'auto') : 'auto' }}
              >
                <div className="carousel-card-item w-full h-full rounded-[2rem] transition-all duration-1000 vertical-align-middle">
                  <div className="text-center rounded-all_48 lg:w-full [&>div]:h-full [&>div]:flex [&>div]:overflow-hidden [&>div]:p-0 py-[48px]" style={{ height: !isMobile ? (maxHeight > 0 ? `${maxHeight}px` : 'auto') : 'auto' }}>
                    {getContent(slide)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="carousel__controls flex w-full justify-center m-0">
        <CarouselArrowButton
          direction="left"
          onClick={prevSlide}
          disabled={isAnimating}
          theme={theme}
          isAnimating={isAnimating}
        />
        <div className={`carousel__dots m-[1rem] flex flex-wrap items-center justify-center gap-[0.5rem] bottom-[1rem] left-0 right-0 z-[10]`}>
          {reorderedSlides.map((_, index) => {
            const { isNow, isPrev, isNext } = getSlidePosition(index);
            return (
              <CarouselDotButton
                key={index}
                index={index}
                currentSlide={currentSlide}
                onClick={(event) => {
                  GTM.carouselClick(event, index);
                  setIsAnimating(true);
                  setSlideDirection(index > currentSlide ? 'next' : 'prev');
                  setCurrentSlide(index);
                  setTimeout(() => setIsAnimating(false), 1000);

                }}
                theme={theme}
                isNow={isNow}
                isPrev={isPrev}
                isNext={isNext}
              />
            );
          })}
        </div>
        <CarouselArrowButton
          direction="right"
          onClick={nextSlide}
          disabled={isAnimating}
          theme={theme}
          isAnimating={isAnimating}
        />
      </div>
    </div>
  );
};

export default Carousel;
